import { createContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { api } from '../utils/api';
import { UnitStatus, UnitType, type DeviceRole } from '../types';

export interface Organization {
  tenantId: string;
  name: string;
  role: string;
  contactEmail?: string;
}

export interface Site {
  id: string; // The SK mapped to id
  tenantId: string;
  name: string;
  location: { lat: number; lng: number };
  status: string;
  apiKey: string;
}

export interface Unit {
  unitId: string;
  nodeId: number;
  nodeName: string;
  unitType: UnitType;
  status: UnitStatus;
  tenantId: string;
  siteId: string;
  lastSeenAt: string;
  adoptedAt?: string;
  tankShape?: string;
  tankDimensions?: { diameter: number; height: number };
  sensorMountHeight?: number;
  currentLevel: number;
  currentVoltage: number;
  previousVoltage?: number;
  location?: { lat: number; lng: number };
  maxBatteryVoltage?: number;
  cadenceSeconds?: number;
  lastRefreshCommandAt?: string;
  /** Present on rev-2 lifecycle-backed Unit views. */
  deviceUuid?: string;
  bindingId?: string;
  role?: DeviceRole;
  serialNumber?: string;
  gatewayId?: string;
}

interface DashboardDataContextType {
  organizations: Organization[];
  activeOrganization: Organization | null;
  changeActiveOrganization: (tenantId: string) => void;
  sites: Site[];
  units: Unit[];
  isLoading: boolean;
  isSuperAdmin: boolean;
  currentUserId: string | null;
  userRoles: any[];
  /** Set when the account or dashboard data load failed. Null when healthy. */
  error: string | null;
  /** Timestamp of the last successful sites/units/telemetry load. */
  lastUpdatedAt: Date | null;
  refetch: () => void;
}

/** How often the dashboard silently re-fetches sites/units/telemetry. */
const REFRESH_INTERVAL_MS = 60_000;

export const DashboardDataContext = createContext<DashboardDataContextType | null>(null);

export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrganization, setActiveOrganization] = useState<Organization | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<any[]>([]);
  // Two error sources so a background-refresh success can't mask an account-load
  // failure (and vice versa). Consumers see them merged as a single `error`.
  const [accountError, setAccountError] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  // Bumped by the polling timer / refetch to re-run the sites+units loader.
  const [refreshCounter, setRefreshCounter] = useState(0);
  // Once we've rendered real data, refreshes happen silently in the background
  // instead of flashing the full-page loading state.
  const hasLoadedOnceRef = useRef(false);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setAccountError(null);
      // IMPORTANT: Pass empty X-Tenant-ID to prevent stale localStorage values
      // from causing 403 Forbidden on these user-scoped endpoints.
      const noTenantHeaders = { headers: { 'X-Tenant-ID': '' } };
      // Both requests must succeed: without tenants the dashboard has no scope,
      // and without the profile we can't resolve roles. A failure here surfaces
      // as an error state with Retry rather than rendering an empty dashboard (D1).
      const [orgs, profileData] = await Promise.all([
        api.get('/api/v1/users/me/tenants', noTenantHeaders),
        api.get('/api/v1/users/me', noTenantHeaders)
      ]);

      console.log('[DashboardData] Loaded orgs:', orgs?.length, orgs);
      console.log('[DashboardData] Loaded profile roles:', profileData?.roles?.length);

      if (profileData && profileData.roles) {
        setUserRoles(profileData.roles);
        const hasSuperAdmin = profileData.roles.some((r: any) => r.role === 'SuperAdmin');
        setIsSuperAdmin(hasSuperAdmin);
      }
      
      if (profileData && profileData.profile) {
        setCurrentUserId(profileData.profile.userId || profileData.profile.PK?.replace('USER#', '') || null);
      }

      setOrganizations(orgs);
      
      if (orgs.length > 0) {
        const savedActiveId = localStorage.getItem('activeTenantId');
        const found = (orgs as Organization[]).find(o => o.tenantId === savedActiveId);
        if (found) {
          setActiveOrganization(found);
        } else {
          // Saved tenant ID is stale or missing — reset to first available org
          localStorage.setItem('activeTenantId', orgs[0].tenantId);
          setActiveOrganization(orgs[0]);
        }
      } else {
        // No orgs — clear any stale tenant ID from localStorage
        localStorage.removeItem('activeTenantId');
        setActiveOrganization(null);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Failed to load initial data', err);
      setAccountError('Could not load your account data. The service may be unreachable.');
      setIsLoading(false);
    }
  }, []);

  // Initial load: Fetch organizations and profile
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const changeActiveOrganization = useCallback((tenantId: string) => {
    if (activeOrganization?.tenantId === tenantId) return;
    const org = organizations.find(o => o.tenantId === tenantId);
    if (org) {
      setActiveOrganization(org);
      localStorage.setItem('activeTenantId', tenantId);
      setSites([]);
      setUnits([]);
      setIsLoading(true);
    }
  }, [organizations, activeOrganization]);

  // Fetch sites and units unconditionally (don't wait for activeOrganization)
  useEffect(() => {
    let isMounted = true;

    async function loadDashboardData() {
      // Only show the blocking loading state before the first successful load
      // (or after an org switch, which resets it explicitly). Timed refreshes
      // run silently so the dashboard doesn't flash every minute (D2).
      if (!hasLoadedOnceRef.current) setIsLoading(true);
      try {
        const reqOptions = { headers: { 'X-Tenant-ID': '' } };
        // Fetch sites
        const sitePromises = [api.get('/api/v1/sites', reqOptions)];
        if (isSuperAdmin) {
          sitePromises.push(api.get('/api/v1/sites/unassigned', reqOptions).catch(() => []));
        }

        const sitesResults = await Promise.all(sitePromises);
        const sitesData: any[] = sitesResults[0] || [];
        const unassignedSitesData: any[] = sitesResults[1] || [];

        // Build a set of all site IDs that have assignments
        const assignedSiteIds = new Set<string>();
        sitesData.forEach(s => {
          const id = (s.PK?.startsWith('SITE#') ? s.PK : s.SK || '').replace('SITE#', '');
          if (id) assignedSiteIds.add(id);
        });

        const mappedSites: any[] = [];
        
        // Add assigned site records
        sitesData.forEach(s => {
          mappedSites.push({
            ...s,
            id: (s.PK?.startsWith('SITE#') ? s.PK : s.SK || '').replace('SITE#', '')
          });
        });

        // Add unassigned site records only if they don't have assignments
        unassignedSitesData.forEach(s => {
          const id = (s.PK?.startsWith('SITE#') ? s.PK : s.SK || '').replace('SITE#', '');
          if (id && !assignedSiteIds.has(id)) {
            mappedSites.push({
              ...s,
              id
            });
          }
        });
        
        if (!isMounted) return;
        setSites(mappedSites);

        // Fetch units & latest telemetry for each site
        const allUnits: Unit[] = [];
        for (const site of mappedSites) {
          const reqOptions = {
            headers: { 'X-Tenant-ID': '' }
          };
          
          const [unitsResult, telemResult] = await Promise.all([
            api.get(`/api/v1/sites/${site.id}/units`, reqOptions),
            api.get(`/api/v1/sites/${site.id}/telemetry/latest`, reqOptions)
          ]);
          
          const telemMap = new Map();
          const telemPrevMap = new Map();
          (telemResult || []).forEach((t: any) => {
            telemMap.set(t.unitId, t.latestTelemetry);
            telemPrevMap.set(t.unitId, t.previousTelemetry);
          });

          const mappedUnits = (unitsResult || []).map((u: any) => {
            const unitId = u.SK ? u.SK.replace('UNIT#', '') : u.GSI2PK.replace('UNIT#', '');
            const telem = telemMap.get(unitId);
            const prevTelem = telemPrevMap.get(unitId);
            return {
              ...u,
              unitId,
              tenantId: site.tenantId || u.tenantId || '', // Map unit tenantId to the parent site's tenantId context
              currentLevel: telem?.measurement || 0,
              currentVoltage: telem?.voltage || 0,
              previousVoltage: prevTelem?.voltage || 0,
              lastSeenAt: telem?.deviceTimestamp || telem?.ingestedAt || u.lastSeenAt
            } as Unit;
          });
          
          allUnits.push(...mappedUnits);
        }

        if (!isMounted) return;
        setUnits(allUnits);
        setDataError(null);
        setLastUpdatedAt(new Date());
        hasLoadedOnceRef.current = true;
      } catch (err) {
        console.error('Failed to load dashboard data', err);
        // Keep any previously loaded data on screen — the consumer shows a
        // "couldn't refresh" banner instead of wiping the dashboard (D1).
        if (isMounted) setDataError('Could not load dashboard data. The service may be unreachable.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    // Always attempt to load dashboard data.
    // The backend only returns sites the user has access to.
    loadDashboardData();

    return () => { isMounted = false; };
  }, [isSuperAdmin, organizations, activeOrganization, refreshCounter]); // Trigger when organizations are loaded, active organization changes, SuperAdmin status changes, or a refresh is requested

  // Auto-refresh (D2): silently re-fetch on an interval so telemetry and
  // "last seen" stay current. Skips ticks while the tab is hidden and
  // refreshes immediately when it becomes visible again.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setRefreshCounter(c => c + 1);
      }
    }, REFRESH_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setRefreshCounter(c => c + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Filter sites and units by the active organization so that pages only see the active context.
  // Also include any sites the user has direct access to via SiteRoles.
  const filteredSites = useMemo(() => {
    if (isSuperAdmin) return sites;
    if (!activeOrganization) {
      if (organizations.length === 0) return sites;
      return [];
    }
    const directSiteIds = new Set(
      userRoles.filter(r => r.SK && r.SK.includes('#SITE#')).map(r => r.SK.split('#SITE#')[1])
    );
    return sites.filter(s => s.tenantId === activeOrganization.tenantId || directSiteIds.has(s.id));
  }, [sites, activeOrganization, isSuperAdmin, organizations, userRoles]);

  const filteredUnits = useMemo(() => {
    if (isSuperAdmin) return units;
    if (!activeOrganization) {
      if (organizations.length === 0) return units;
      return [];
    }
    const directSiteIds = new Set(
      userRoles.filter(r => r.SK && r.SK.includes('#SITE#')).map(r => r.SK.split('#SITE#')[1])
    );
    return units.filter(u => u.tenantId === activeOrganization.tenantId || directSiteIds.has(u.siteId));
  }, [units, activeOrganization, isSuperAdmin, organizations, userRoles]);

  const refetch = useCallback(() => {
    loadInitialData();
  }, [loadInitialData]);

  return (
    <DashboardDataContext.Provider
      value={{
        organizations,
        activeOrganization,
        changeActiveOrganization,
        sites: filteredSites,
        units: filteredUnits,
        isLoading,
        isSuperAdmin,
        currentUserId,
        userRoles,
        error: accountError ?? dataError,
        lastUpdatedAt,
        refetch
      }}
    >
      {children}
    </DashboardDataContext.Provider>
  );
}
